import { createLightTheme, createDarkTheme } from '@fluentui/react-components'
import type { BrandVariants, Theme } from '@fluentui/react-components'

/**
 * DACcelerate brand ramp for Fluent 2.
 *
 * Primary = "haze blue" (雾霾蓝): a muted, dusty slate-blue — calmer and
 * softer than the old hard navy (#143a6f), while staying a Bayer-adjacent
 * cool blue. The under-used accent gold has been retired.
 *
 * The 16-shade ramp (10–160) matches Fluent 2's BrandVariants contract;
 * brand-80 (#4c6a80) is the hero action shade (white-text-safe ~4.9:1).
 */
export const bayerBrand: BrandVariants = {
  10: '#070b0e',
  20: '#121b21',
  30: '#1c2833',
  40: '#253544',
  50: '#2f4152',
  60: '#394d60',
  70: '#42596e',
  80: '#4c6a80',
  90: '#5c7b90',
  100: '#6d8b9f',
  110: '#8199ab',
  120: '#98adbc',
  130: '#b1c1cd',
  140: '#cad4dd',
  150: '#e1e7ec',
  160: '#eff3f6',
}

/** Crisper corners across every Fluent component — tighten the radius
 *  tokens toward the design's sharp, flat look (Small/Circular unchanged). */
const crispRadii = {
  borderRadiusMedium: '3px',
  borderRadiusLarge: '4px',
  borderRadiusXLarge: '6px',
} as const

export const bayerLightTheme: Theme = {
  ...createLightTheme(bayerBrand),
  ...crispRadii,
}

export const bayerDarkTheme: Theme = {
  ...createDarkTheme(bayerBrand),
  ...crispRadii,
}

/* ─── Bayer Design Tokens ─────────────────────────────────────────────
 *
 * Convention: use Fluent `tokens.*` for anything Fluent ships
 * (neutrals, brand variants, spacing, radii, shadows, typography,
 * motion). Reach for these exports when the design calls for a
 * Bayer-specific shade that Fluent's derived palette doesn't expose.
 *
 * The `bayerColors.*` names below carry the haze-blue primary for the many
 * call sites that reference a fixed brand hex; prefer them over hardcoding.
 * (Names kept as `navy*` for churn-free import compatibility.)
 * ──────────────────────────────────────────────────────────────── */

/** Brand shades used outside the Fluent-managed tokens (haze blue).
 *  navy = the primary; navyMid = links/secondary; navyLight = tint bg.
 *  Each reads a CSS variable so the admin Look & feel drawer can move it with
 *  the brand colour (lib/lookAndFeel.ts); unset, the hex after it holds. That
 *  makes these CSS values only — no colour maths or `${hex}20` alpha on them. */
export const bayerColors = {
  navy: 'var(--dac-navy, #46647e)', // haze blue — section titles, primary headings, marks
  navyMid: 'var(--dac-navy-mid, #54749a)', // dusty blue — secondary text, hyperlink color
  navyLight: 'var(--dac-navy-light, #eaeef5)', // very light haze tint — notice strips, panels
} as const

/** Priority bucket palette — semantic categories without a Fluent
 *  equivalent. Mirrors the --bucket-* CSS vars in index.css; keep
 *  them in sync if you change either side. */
export const bucketColors = {
  majorUnlocks: '#7c3aed', // purple
  highPriority: '#dc2626', // red
  quickWins: '#16a34a', // green
  parkingLot: '#ca8a04', // gold
  declined: '#6b7280', // gray
} as const

/** Repeated semantic surface backgrounds + borders. Use a named
 *  entry instead of hardcoding the hex per call site so a future
 *  redesign happens in one place. */
export const surfaces = {
  // Sticky strip at top of a form/tab while edits are buffered
  saveBarBg: '#eaeef5',
  saveBarBorder: '#cdd8e5',
  // Yellow info panel — bulk-mode banner on ApplicationsTab and similar
  bulkPanelBg: '#fff8e1',
  bulkPanelBorder: '#ffe082',
  // Soft brand-tinted card — applicant proposal section, similar
  proposalCardBg: '#eef2f7',
} as const

/** Retired accent gold — kept as aliases (now haze) so any stray import
 *  resolves; the gold accent is no longer part of the design language. */
export const accentGold = '#54749a'
export const accentGoldHover = '#46647e'
export const accentGoldLight = '#eaeef5'

/** Sidebar — dark inverted slate (tuned to the haze-blue primary). The
 *  backgrounds read CSS variables for the Look & feel drawer, as above; the
 *  text stays put, so the drawer offers dark backgrounds only. */
export const sidebarBg = 'var(--dac-sidebar-bg, #283744)'
export const sidebarBgHover = 'var(--dac-sidebar-bg-hover, #31404d)'
export const sidebarBgActive = 'var(--dac-sidebar-bg-active, #37485a)'
export const sidebarText = '#a7b4c2'
export const sidebarTextActive = '#ffffff'
export const sidebarBorder = 'var(--dac-sidebar-border, #1e2a35)'

/** Layout tokens */
export const contentBg = '#f5f6fa'

/** Semantic colors */
export const semanticSuccess = '#2e7d32'
export const semanticWarning = '#e65100'
export const semanticDanger = '#c62828'
export const semanticInfo = '#1565c0'
